(() => {
  const cache = new Map();
  const debounceMs = 220;
  const sceneHoverDelay = 350;

  function buildUrlFromForm(form) {
    const url = new URL(form.action, window.location.origin);
    const data = new FormData(form);
    for (const [key, value] of data.entries()) {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    }
    url.searchParams.delete('page');
    return url;
  }

  function activateImage(image) {
    if (!image || !image.dataset.src) return;
    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      image.removeAttribute('data-srcset');
    }
    image.src = image.dataset.src;
    image.removeAttribute('data-src');
  }

  function initRoot(root) {
    if (!root || root.dataset.lpeReady === 'true') return;
    root.dataset.lpeReady = 'true';

    const sectionId = root.dataset.sectionId;
    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    let controller = null;
    let debounceTimer = null;
    let sceneTimer = null;
    let imageObserver = null;

    const loadingNode = () => root.querySelector('[data-lpe-loading]');
    const drawerNode = () => root.querySelector('[data-lpe-filter-drawer]');
    const overlayNode = () => root.querySelector('[data-lpe-filter-overlay]');
    const openButton = () => root.querySelector('[data-lpe-filter-open]');

    function observeDeferredImages(scope = root) {
      const images = scope.querySelectorAll('img[data-lpe-image][data-src]');
      if (!images.length) return;

      if (!('IntersectionObserver' in window)) {
        images.forEach(activateImage);
        return;
      }

      if (!imageObserver) {
        imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            activateImage(entry.target);
            observer.unobserve(entry.target);
          });
        }, { rootMargin: '300px 0px', threshold: 0.01 });
      }

      images.forEach((image) => imageObserver.observe(image));
    }

    function loadSceneImage(trigger) {
      if (!supportsHover.matches) return;
      activateImage(trigger?.querySelector('[data-lpe-scene-image]'));
    }

    function setLoading(state) {
      root.setAttribute('aria-busy', state ? 'true' : 'false');
      const node = loadingNode();
      if (node) node.hidden = !state;
    }

    function openDrawer() {
      const drawer = drawerNode();
      const overlay = overlayNode();
      if (!drawer || !overlay) return;
      drawer.hidden = false;
      overlay.hidden = false;
      document.documentElement.classList.add('lpe-scroll-lock');
      openButton()?.setAttribute('aria-expanded', 'true');
    }

    function closeDrawer() {
      const drawer = drawerNode();
      const overlay = overlayNode();
      if (drawer) drawer.hidden = true;
      if (overlay) overlay.hidden = true;
      document.documentElement.classList.remove('lpe-scroll-lock');
      openButton()?.setAttribute('aria-expanded', 'false');
    }

    function dialogForTrigger(trigger) {
      const dialogId = trigger?.getAttribute('aria-controls');
      return dialogId ? document.getElementById(dialogId) : null;
    }

    function openScene(trigger) {
      const dialog = dialogForTrigger(trigger);
      if (!dialog || dialog.open) return;
      window.clearTimeout(sceneTimer);
      dialog.showModal();
      document.documentElement.classList.add('lpe-scene-open');
    }

    function closeScene(dialog) {
      if (!dialog?.open) return;
      dialog.close();
      document.documentElement.classList.remove('lpe-scene-open');
    }

    async function fetchSection(targetUrl, options = {}) {
      const { pushState = true, scrollToResults = false, preserveScroll = false } = options;
      const cleanUrl = new URL(targetUrl, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      const cacheKey = cleanUrl.pathname + cleanUrl.search;
      const previousScrollY = window.scrollY;
      setLoading(true);

      try {
        let html = cache.get(cacheKey);
        if (!html) {
          controller?.abort();
          controller = new AbortController();
          const renderUrl = new URL(cleanUrl);
          renderUrl.searchParams.set('section_id', sectionId);
          const response = await fetch(renderUrl.toString(), {
            signal: controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          html = await response.text();
          cache.set(cacheKey, html);
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = doc.querySelector('[data-lpe-root]');
        if (!nextRoot) throw new Error('Product Explorer section not found in response.');

        imageObserver?.disconnect();
        root.innerHTML = nextRoot.innerHTML;
        observeDeferredImages();

        if (preserveScroll) {
          requestAnimationFrame(() => window.scrollTo({ top: previousScrollY, behavior: 'auto' }));
        }
        if (pushState) {
          window.history.pushState({}, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
        }
        closeDrawer();
        if (scrollToResults) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[LUFASND Product Explorer]', error);
        window.location.assign(cleanUrl.toString());
      } finally {
        setLoading(false);
      }
    }

    function submitAjax(form) {
      if (form) fetchSection(buildUrlFromForm(form));
    }

    root.addEventListener('pointerover', (event) => {
      if (!supportsHover.matches) return;

      const media = event.target.closest('[data-lpe-scene-load]');
      if (media && !media.contains(event.relatedTarget)) loadSceneImage(media);

      const trigger = event.target.closest('[data-lpe-scene-trigger]');
      if (!trigger || trigger.contains(event.relatedTarget)) return;
      window.clearTimeout(sceneTimer);
      sceneTimer = window.setTimeout(() => openScene(trigger), sceneHoverDelay);
    });

    root.addEventListener('pointerout', (event) => {
      if (!supportsHover.matches) return;
      const trigger = event.target.closest('[data-lpe-scene-trigger]');
      if (!trigger || trigger.contains(event.relatedTarget)) return;
      window.clearTimeout(sceneTimer);
    });

    root.addEventListener('focusin', (event) => {
      const media = event.target.closest('[data-lpe-scene-load]');
      if (media) loadSceneImage(media);
    });

    root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-lpe-filter-form], [data-lpe-sort-form]');
      if (!form) return;
      event.preventDefault();
      submitAjax(form);
    });

    root.addEventListener('change', (event) => {
      const sort = event.target.closest('[data-lpe-sort]');
      if (sort) return submitAjax(sort.form);
      const filterInput = event.target.closest('[data-lpe-filter-input]');
      if (!filterInput) return;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => submitAjax(filterInput.form), debounceMs);
    });

    root.addEventListener('click', (event) => {
      const sceneClose = event.target.closest('[data-lpe-scene-close]');
      if (sceneClose) {
        event.preventDefault();
        closeScene(sceneClose.closest('[data-lpe-scene-dialog]'));
        return;
      }

      const sceneTrigger = event.target.closest('[data-lpe-scene-trigger]');
      if (sceneTrigger) {
        event.preventDefault();
        openScene(sceneTrigger);
        return;
      }

      if (event.target.closest('[data-lpe-filter-open]')) {
        event.preventDefault();
        openDrawer();
        return;
      }
      if (event.target.closest('[data-lpe-filter-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.target.closest('[data-lpe-filter-overlay]')) {
        closeDrawer();
        return;
      }

      const pageLink = event.target.closest('[data-lpe-page-link]');
      if (pageLink) {
        event.preventDefault();
        fetchSection(pageLink.href, { scrollToResults: true });
        return;
      }

      const categoryLink = event.target.closest('[data-lpe-category-link]');
      if (categoryLink) {
        event.preventDefault();
        closeDrawer();
        fetchSection(categoryLink.href, { preserveScroll: true });
        return;
      }

      const clearLink = event.target.closest('[data-lpe-clear]');
      if (clearLink) {
        event.preventDefault();
        fetchSection(clearLink.href);
      }
    });

    root.addEventListener('click', (event) => {
      const dialog = event.target.closest('[data-lpe-scene-dialog]');
      if (dialog && event.target === dialog) closeScene(dialog);
    });

    root.addEventListener('close', (event) => {
      if (event.target.matches?.('[data-lpe-scene-dialog]')) {
        document.documentElement.classList.remove('lpe-scene-open');
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
    });

    window.addEventListener('popstate', () => {
      fetchSection(window.location.href, { pushState: false });
    });

    observeDeferredImages();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-lpe-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
